import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { toast } from 'react-toastify'
import { FaFileExport } from "react-icons/fa"
import Server from "../../Shared/Server"
import Spinner from '../../Shared/Spinner'
import SelectField from "../../Shared/SelectField";
import {years_list, months_list, categories_with_serverside} from "../../Shared/List";

import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const Billed = () => {
    const current_user = JSON.parse(localStorage.getItem('user'))
    const billing_date  = current_user?.branch?.billing_date
    const [errors, setErrors] = useState({});
    const [billed, setBilled] = useState([]);
    const [title, setTitle] = useState('');
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([{}])
    const [usageHeaderName, setUsageHeaderName] = useState('Usage')
    const [yearOptions, setYearOptions] = useState([]);
    const [monthOptions, setMonthOptions] = useState([]);
    const [byOptions, setByOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const gridRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: function (params) {
                if (params.node.rowPinned) return '';
                if (isNaN(params.node.id)) {
                    return "";
                }
                return `${Number(params.node.rowIndex) + 1}`;
            },
        },
        {
            headerName: "Name",
            field: "name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Customers",
            field: "customers",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: usageHeaderName,
            field: "usage",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "P.Balance",
            field: "previous_balance",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Bill",
            field: "bill",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: "Total",
            field: "total",
            sortable: true,
            filter: 'agNumberColumnFilter',
        },
    ], [usageHeaderName])

    const [formData, setFormData] = useState({
        year: billing_date?.year,
        month: billing_date?.month,
        by: 1,
    })
    const {year, month, by} = formData

    const fetchData = async (payload) => {
        setIsLoading(true)
        try {
            const response = await Server.post('/billed/', payload)
            const years = []
            const months = []
            const categories = []

            years_list.map(year => (
                years.push({
                    id: year.id,
                    key: year.key,
                    name: year.name
                })
            ))

            months_list.map(month => (
                months.push({
                    id: month.id,
                    key: month.key,
                    name: month.name
                })
            ))

            categories_with_serverside.map(category => (
                categories.push({
                    id: category.id,
                    key: category.key,
                    name: category.name
                })
            ))

            setBilled(response?.data)
            setYearOptions(years)
            setMonthOptions(months)
            setByOptions(categories)
            setIsLoading(false)

            const output = response?.data.reduce((data, row) => {
                data.customers += parseFloat(row.customers);
                data.usage += parseFloat(row.usage);
                data.previous_balance += parseFloat(row.previous_balance);
                data.bill += parseFloat(row.bill);
                data.total += parseFloat(row.total);
                return data;
            }, {customers: 0, usage: 0.00, previous_balance: 0.00, bill: 0.00, total: 0});
            setPinnedBottomRowData([
                {
                    customers: parseFloat(output.customers).toFixed(0),
                    usage: parseFloat(output.usage).toFixed(0),
                    bill: parseFloat(output.bill).toFixed(2),
                    total: parseFloat(output.total).toFixed(2),
                    previous_balance: parseFloat(output.previous_balance).toFixed(2)
                }
            ]);

            const newUsageHeaderName = response?.data[0]?.branch?.unit;
            if (newUsageHeaderName) {
                setUsageHeaderName(newUsageHeaderName);
            }
        } catch (error) {
            setIsLoading(false)
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 422) {
                setErrors({})
                error.response?.data?.detail.map((err) => (
                    setErrors((prevState) => ({
                        ...prevState,
                        [err.loc[1]]: err.msg
                    }))
                ))
            }else if (error?.response?.status === 403){
                toast.error(error?.response?.data?.detail || 'Forbidden');
                navigate('/');
            }else if (error?.response?.status === 402){
                toast.error(error?.response?.data?.detail || 'Payment required');
                navigate('/')
            }else {
                toast.warning(error?.response?.data?.detail || 'Failed to load billed data')
            }
        }
    }

    useEffect(() => {
        if (location.state?.year && location.state?.month) {
            const matchedCategory = typeof location.state.by === 'string'
                ? categories_with_serverside.find(c => c.name.toLowerCase() === location.state.by.toLowerCase())
                : null;
            const byId = matchedCategory?.id || (typeof location.state.by === 'number' ? location.state.by : 1);
            const byStr = matchedCategory?.name || categories_with_serverside[byId - 1].name;
            setFormData(prev => ({
                ...prev,
                year: location.state.year,
                month: location.state.month,
                by: byId,
            }))
            const monthName = months_list[location.state.month - 1].name
            setTitle(`${monthName}, ${location.state.year} Billed By ${byStr}`);
            fetchData({ year: location.state.year, month: location.state.month, by: byStr })
        } else {
            const monthName = months_list[formData.month - 1].name
            const categoryName = categories_with_serverside[formData.by - 1].name
            setTitle(`${monthName}, ${year} Billed By ${categoryName}`);
            fetchData({year: year, month: month, by: categoryName})
        }

    }, [])

      const onChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))
    }

    const onDoubleClick = (payload) => {
        navigate(`/billed-${payload.by}`, { state: {data: payload}})
    }

    const onSubmit = (e) => {
        e.preventDefault()

        const monthName = months_list[formData.month - 1].name
        const categoryName = categories_with_serverside[formData.by - 1].name
        setTitle(`${monthName}, ${year} Billed By ${categoryName}`);
        if (categoryName === 'ServerSide'){
            const payload = {year, month, by: 'serverside', name: 'ServerSide'}
            navigate(`/billed-serverside`, { state: {data: payload}})
        }else{
            const data = {year, month, by: categoryName}
            fetchData(data)
        }

    }

    const gridApiRef = useRef(null);

    const onGridReady = (params) => {
        gridApiRef.current = params.api;
    };

    const onExportClick = () => {
        if (gridApiRef.current) {
            gridApiRef.current.exportDataAsCsv({ fileName: `${title}.csv` });
        } else {
            console.error("Grid API is not available yet!");
        }
    };

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <form onSubmit={onSubmit}>
            <div className="row">
                <div className="col-md-2">
                    <SelectField
                        name="year"
                        value={parseInt(year)}
                        options={yearOptions}
                        onChange={onChange}
                        error={errors.year}
                    />
                </div>
                <div className="col-md-2">
                    <SelectField
                        name="month"
                        value={parseInt(month)}
                        options={monthOptions}
                        onChange={onChange}
                        error={errors.month}
                    />
                </div>
                <div className="col-md-2">
                    <SelectField
                        name="by"
                        value={parseInt(by)}
                        options={byOptions}
                        onChange={onChange}
                        error={errors.by}
                    />
                </div>
                <div className="col-md-4">
                    <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
                </div>
                <div className="col-md-2">
                    <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
                </div>
            </div>
            <br/>
        </form>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                    <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            onRowDoubleClicked={(e) => {
                                if (e.node.rowPinned) return '';
                                if (e.data.customers < 1) return '';
                                onDoubleClick({
                                    year: parseInt(year),
                                    month: parseInt(month),
                                    public_id: e.data.public_id,
                                    by: categories_with_serverside[formData.by - 1].name.toLowerCase(),
                                    name: e.data.name
                                })
                            }}

                            suppressMenuHide={true}

                            rowData={billed}
                            domLayout={"normal"}
                            rowSelection={"multiple"}

                            animateRows={true}

                            pagination={false}
                            onGridReady={onGridReady}

                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}


                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>
      <br/>
    </>
  )
}

export default Billed
